/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Alert, Badge, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from "reactstrap";

import useConfig from "../../../actions/Tenants/config/configHook";
import { Button } from "../../Helper/Button";
import { Loading } from "../../Helper/Loading";
import { CATEGORY_COLOR, CATEGORY_LABEL, SignalCategory } from "../Signals/signalTypes";
import {
    buildParamsOverride,
    GlobalRule,
    RuleTenantState,
    useRuleTenant,
    useSignalRules,
    useUpdateRuleTenant,
} from "../Signals/signalRulesHooks";

const display = (value: any) => (value === null || value === undefined ? "—" : String(value));

interface RuleEditModalProps {
    rule: GlobalRule;
    tenantState: RuleTenantState;
    isOpen: boolean;
    onClose: () => void;
    onSave: (override: Record<string, any> | null) => void;
    isSaving?: boolean;
}

const RuleEditModal: React.FC<RuleEditModalProps> = ({ rule, tenantState, isOpen, onClose, onSave, isSaving }) => {
    const globalParams = tenantState.global_params || rule.params || {};
    const override = tenantState.params_override || {};
    const paramKeys = Object.keys(globalParams);

    const buildDraft = () => {
        const draft: Record<string, string> = {};
        paramKeys.forEach((key) => {
            const effective = override[key] ?? globalParams[key];
            draft[key] = effective === null || effective === undefined ? "" : String(effective);
        });
        return draft;
    };
    const [draft, setDraft] = useState<Record<string, string>>(buildDraft);
    useEffect(() => setDraft(buildDraft()), [tenantState]);

    const { override: builtOverride, errors } = buildParamsOverride(draft, globalParams);
    const hasErrors = Object.keys(errors).length > 0;

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered>
            <ModalHeader toggle={onClose}>Edit thresholds — {rule.key}</ModalHeader>
            <ModalBody>
                {paramKeys.length === 0 && <div className="text-muted">This rule has no thresholds.</div>}
                {paramKeys.map((key) => (
                    <div key={key} className="mb-3">
                        <label className="form-label small fw-bold text-capitalize">{key.replace(/_/g, " ")}</label>
                        <div className="d-flex gap-2 align-items-center">
                            <Input
                                bsSize="sm"
                                value={draft[key] ?? ""}
                                invalid={!!errors[key]}
                                aria-label={key}
                                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                            />
                            <Button
                                color="link"
                                size="sm"
                                onClick={() => setDraft((d) => ({ ...d, [key]: display(globalParams[key]) === "—" ? "" : String(globalParams[key]) }))}
                            >
                                Reset
                            </Button>
                        </div>
                        <div className="small text-muted">Global default: {display(globalParams[key])}</div>
                        {errors[key] && <div className="small text-danger">{errors[key]}</div>}
                    </div>
                ))}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onClose}>Cancel</Button>
                <Button color="primary" disabled={hasErrors} isLoading={isSaving} onClick={() => onSave(builtOverride)}>Save</Button>
            </ModalFooter>
        </Modal>
    );
};

const RuleRow: React.FC<{ rule: GlobalRule; tenant: string }> = ({ rule, tenant }) => {
    const { data: tenantState } = useRuleTenant(rule.key, tenant);
    const updateRule = useUpdateRuleTenant(tenant);
    const [editOpen, setEditOpen] = useState(false);

    const enabled = tenantState?.enabled ?? rule.enabled;

    const onToggle = () => {
        if (!tenantState) return;
        updateRule.mutate({ ruleKey: rule.key, enabled: !enabled, params_override: tenantState.params_override });
    };

    const onSave = (override: Record<string, any> | null) => {
        updateRule.mutate(
            { ruleKey: rule.key, enabled, params_override: override },
            { onSuccess: () => setEditOpen(false) },
        );
    };

    return (
        <tr>
            <td className="align-middle">{rule.key}</td>
            <td className="align-middle">
                <Badge color={CATEGORY_COLOR[rule.category as SignalCategory] || "secondary"}>
                    {CATEGORY_LABEL[rule.category as SignalCategory] || rule.category}
                </Badge>
            </td>
            <td className="align-middle">
                <Input
                    type="switch"
                    role="switch"
                    checked={enabled}
                    disabled={!tenantState}
                    aria-label={`Enable ${rule.key}`}
                    onChange={onToggle}
                />
            </td>
            <td className="align-middle">
                <Button color="light" size="sm" disabled={!tenantState} onClick={() => setEditOpen(true)}>Edit</Button>
                {tenantState && (
                    <RuleEditModal
                        rule={rule}
                        tenantState={tenantState}
                        isOpen={editOpen}
                        onClose={() => setEditOpen(false)}
                        onSave={onSave}
                        isSaving={updateRule.isPending}
                    />
                )}
            </td>
        </tr>
    );
};

export const SignalRulesAdmin: React.FC = () => {
    const user = useSelector((state: any) => state.auth.user);
    const config = useConfig();
    const tenant = config?.tenant_id;
    const { data: rules, isLoading } = useSignalRules();

    if (!user?.is_admin) {
        return <Alert color="warning">This page is available to EFQ consultants only.</Alert>;
    }

    // Master gate (EFQ-4145): only relevant once the tenant has Signals enabled.
    if (!config?.root_config?.ripening_room_enable_signals) {
        return <Alert color="warning">Signals is not enabled for this tenant.</Alert>;
    }

    return (
        <div className="p-3">
            <h4 className="mb-1">Signal Rules</h4>
            <p className="text-muted small">Enable / disable rules and edit thresholds for the active tenant.</p>

            {isLoading ? (
                <Loading />
            ) : (
                <Table size="sm" responsive>
                    <thead>
                        <tr>
                            <th>Rule</th>
                            <th>Category</th>
                            <th>Enabled</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {(rules ?? []).map((rule) => (
                            <RuleRow key={rule.key} rule={rule} tenant={tenant} />
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default SignalRulesAdmin;
